/**
 * Takeoff API helpers — every call the Estimating 2 tab makes, in one place.
 * Drawings upload in 4 MB chunks (proxy-safe for large plan PDFs) and
 * download through axios blobs (Bearer auth — Android PWAs evict cookies).
 */
import api from "@/lib/api";

export const CHUNK = 4 * 1024 * 1024; // 4 MiB parts — the proxy caps request
// bodies at 8 MiB; 8 MiB chunks died at exactly 8,388,608 bytes. 4 MiB is
// permanently safe.

const fmtEta = (s) => (s == null || !isFinite(s) ? "" : s < 60 ? `${Math.ceil(s)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`);

/** Report a browser-side failure into the shared diagnostics stream so the
 * on-screen error and the backend log carry the same correlation ID. */
function reportClientDiag({ correlationId, kind, stage, detail, file, offset, chunk, httpStatus, retries, lastStep, startedAt }) {
  api.post("/takeoff/diagnostics/client", {
    correlation_id: correlationId, kind, stage, detail,
    name: file?.name, size: file?.size, offset, chunk,
    http_status: httpStatus, retries, last_step: lastStep,
    duration_ms: startedAt ? Date.now() - startedAt : undefined,
    browser: navigator.userAgent, os: navigator.platform, online: navigator.onLine,
  }).catch(() => {});
}

class UploadError extends Error {
  constructor(detail, meta) {
    super(detail);
    this.upload = meta; // {correlationId, stage, offset, chunk, done, total, httpStatus, retries}
  }
}

/**
 * Shared chunk pump — 4 MiB parts, six parallel workers (dropping to one
 * after the first retry), up to MAX_ATTEMPTS per part with bounded exponential backoff (network errors, 408, 429, 5xx
 * only — a 4xx means the request itself is wrong). On a fatal part failure
 * the queue stops, in-flight parts settle, and the error is enriched with
 * the SERVER's confirmed byte state (never the browser's guess) so the
 * error panel's offset/chunk/received are real. Progress events report
 * server-acknowledged bytes.
 * onEvent receives {pct, done, total, chunk, chunkCount, retries, speedBps, etaS}.
 * `confirmed` is a server-provided map of already-landed offsets (resume).
 */
const RETRYABLE = (e) =>
  e.code === "ERR_CANCELED" || e.name === "CanceledError" || // stall watchdog abort — retry, don't fail
  !e.response || [408, 429].includes(e.response.status) || (e.response.status >= 500 && e.response.status < 600);

// Stall watchdog. The browser hands a whole 4 MiB part to the socket buffer
// in a blink, so XHR progress hits 100% long before the bytes actually reach
// the server over a slow site uplink — then we wait on the server's reply
// with NO progress ticks. The old 20 s limit aborted perfectly healthy slow
// chunks ("canceled" after 4 retries). Allow a response window sized for a
// slow link (~50 KB/s ⇒ ~80 s for 4 MiB), never below 60 s.
const STALL_MS = Math.max(60000, Math.round((CHUNK / 51200) * 1000));
const MAX_ATTEMPTS = 8;
/** Parts in flight per file. Six keeps a good connection busy (3 left a
 * fast uplink half idle); the pump still drops to one after any retry so a
 * struggling site link isn't fought over. */
const PART_WORKERS = 6;
/** Files below this size skip the end-of-upload SHA-256 (size + contiguity
 * are still verified server-side). 50 MB. */
export const CHECKSUM_MIN_BYTES = 50 * 1024 * 1024;

/** Thrown when the user pauses: the server session + confirmed ranges stay,
 * so Resume continues from the last confirmed byte without re-sending. */
export class PausedError extends Error {
  constructor(meta) { super("Upload paused"); this.name = "PausedError"; this.paused = true; this.upload = meta; }
}

async function pumpChunks(file, putUrl, { onEvent, confirmed = {}, correlationId, stage = "upload", pauseSignal = null }) {
  const offsets = [];
  for (let off = 0; off < file.size; off += CHUNK) {
    if (confirmed[String(off)] == null) offsets.push(off); // resume: skip confirmed ranges
  }
  const chunkCount = Math.ceil(file.size / CHUNK);
  let done = Object.values(confirmed).reduce((a, b) => a + b, 0);
  const t0 = Date.now();
  const startedBytes = done;
  const emit = (extra = {}) => {
    const secs = (Date.now() - t0) / 1000;
    const speedBps = secs > 0.5 ? (done - startedBytes) / secs : null;
    const etaS = speedBps ? (file.size - done) / speedBps : null;
    onEvent?.({ pct: Math.round((done / file.size) * 100), done, total: file.size, chunkCount, speedBps, etaS, etaText: fmtEta(etaS), ...extra });
  };
  emit({ chunk: 0, retries: 0 });
  let fatal = null; // first unrecoverable part failure — stops the queue
  const putPart = async (offset) => {
    const chunk = file.slice(offset, Math.min(offset + CHUNK, file.size));
    const chunkNo = Math.floor(offset / CHUNK) + 1;
    let delay = 500;
    for (let attempt = 1; ; attempt++) {
      if (fatal) return; // another part already failed — don't pile on
      if (pauseSignal?.aborted) { fatal = new PausedError({ correlationId, stage, offset, chunk: chunkNo, done, total: file.size }); return; }
      try {
        // Stall watchdog: no upload progress for STALL_MS => hung socket.
        // Abort it ourselves so the retry loop recovers in seconds instead
        // of the browser sitting on a dead connection for minutes.
        const controller = new AbortController();
        let lastTick = Date.now();
        const onPause = () => controller.abort();
        pauseSignal?.addEventListener("abort", onPause, { once: true });
        const watchdog = setInterval(() => {
          if (Date.now() - lastTick > STALL_MS) controller.abort();
        }, 2000);
        let res;
        try {
          res = await api.put(`${putUrl}?offset=${offset}`, chunk, {
            headers: { "Content-Type": "application/octet-stream" },
            signal: controller.signal,
            onUploadProgress: () => { lastTick = Date.now(); },
          });
        } finally {
          clearInterval(watchdog);
          pauseSignal?.removeEventListener("abort", onPause);
        }
        // progress = server-acknowledged total, kept monotonic (a fast small
        // chunk can answer before a slow sibling's accounting lands)
        const serverReceived = res?.data?.received;
        done = typeof serverReceived === "number" ? Math.max(done, serverReceived) : done + chunk.size;
        emit({ chunk: chunkNo, retries: attempt - 1 });
        return;
      } catch (e) {
        if (pauseSignal?.aborted) {
          // user pressed Pause mid-part — not a failure, nothing to report
          fatal = new PausedError({ correlationId, stage, offset, chunk: chunkNo, done, total: file.size });
          return;
        }
        if (!RETRYABLE(e) || attempt >= MAX_ATTEMPTS) {
          const detail = e?.response?.data?.detail || e.message || "network error";
          const httpStatus = e?.response?.status;
          reportClientDiag({
            correlationId, kind: stage, stage: "chunk upload",
            detail: `Upload stopped at byte ${offset.toLocaleString()} (chunk ${chunkNo} of ${chunkCount}): ${detail}`,
            file, offset, chunk: chunkNo, httpStatus, retries: attempt - 1,
            lastStep: "chunk upload", startedAt: t0,
          });
          fatal = new UploadError(detail, {
            correlationId, stage, offset, chunk: chunkNo,
            done, total: file.size, httpStatus, retries: attempt - 1,
          });
          return;
        }
        emit({ retries: attempt });
        // A retry means the link is struggling — finish this file one part at
        // a time so the parallel workers stop competing for the same uplink.
        maxWorkers = 1;
        await new Promise((r) => setTimeout(r, delay));
        delay = Math.min(delay * 2, 15000);
      }
    }
  };
  const queue = [...offsets];
  let maxWorkers = Math.min(PART_WORKERS, queue.length);
  const workers = Array.from({ length: Math.min(PART_WORKERS, queue.length) }, async (_, idx) => {
    while (queue.length && !fatal) {
      if (idx >= maxWorkers) return; // throttled: let worker 0 carry the rest
      await putPart(queue.shift());
    }
  });
  await Promise.all(workers); // workers never throw — fatal carries the error
  if (fatal) throw fatal;
}

/** SHA-256 of a File via WebCrypto — sent to the server at completion,
 * which refuses to finalize a file whose stored bytes don't match. */
async function sha256File(file) {
  try {
    const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null; // older browsers — server skips the check when absent
  }
}


export const loadWorkspace = (design = false) => api.get("/takeoff/workspace", { params: design ? { design: 1 } : {} }).then((r) => r.data);

export const createProject = (p) => api.post("/takeoff/projects", p).then((r) => r.data);
export const updateProject = (id, p) => api.patch(`/takeoff/projects/${id}`, p).then((r) => r.data);
export const deleteProject = (id) => api.delete(`/takeoff/projects/${id}`).then((r) => r.data);
export const setProjectDesign = (id, design) => api.put(`/takeoff/projects/${id}/design`, { design }).then((r) => r.data);
export const designToEstimate = (id) => api.post(`/takeoff/projects/${id}/to-estimate`).then((r) => r.data);
export const listDesignDrawings = () => api.get("/takeoff/design/drawings").then((r) => r.data);
export const createBlankCad = (pid, p) => api.post(`/takeoff/projects/${pid}/cad/blank`, p).then((r) => r.data);

export const createFolder = (projectId, name, { parentId = null, category = "plans" } = {}) =>
  api.post(`/takeoff/projects/${projectId}/folders`, { name, parentId, category }).then((r) => r.data);
export const renameFolder = (id, name) => api.patch(`/takeoff/folders/${id}`, { name }).then((r) => r.data);
export const moveFolder = (id, parentId) => api.patch(`/takeoff/folders/${id}`, { parentId }).then((r) => r.data);
export const deleteFolder = (id) => api.delete(`/takeoff/folders/${id}`).then((r) => r.data);
export const moveDrawings = (ids, folderId, category = null) =>
  api.post(`/takeoff/drawings/move`, { drawing_ids: ids, folder_id: folderId, ...(category ? { category } : {}) }).then((r) => r.data);

export const loadState = (projectId) => api.get(`/takeoff/projects/${projectId}/state`).then((r) => r.data);
export const saveState = (projectId, data) => api.put(`/takeoff/projects/${projectId}/state`, data).then((r) => r.data);

export const listItems = (projectId) => api.get(`/takeoff/projects/${projectId}/items`).then((r) => r.data);
export const addItem = (projectId, item) => api.post(`/takeoff/projects/${projectId}/items`, item).then((r) => r.data);
export const updateItem = (id, patch) => api.patch(`/takeoff/items/${id}`, patch).then((r) => r.data);
export const deleteItem = (id) => api.delete(`/takeoff/items/${id}`).then((r) => r.data);

export const addLibraryItem = (item) => api.post("/takeoff/library", item).then((r) => r.data);
export const updateLibraryItem = (id, patch) => api.patch(`/takeoff/library/${id}`, patch).then((r) => r.data);
export const deleteLibraryItem = (id) => api.delete(`/takeoff/library/${id}`).then((r) => r.data);

export const listDrawings = (projectId) => api.get(`/takeoff/projects/${projectId}/drawings`).then((r) => r.data);
export const updateDrawing = (id, patch) => api.patch(`/takeoff/drawings/${id}`, patch).then((r) => r.data);
export const deleteDrawing = (id) => api.delete(`/takeoff/drawings/${id}`).then((r) => r.data);

export const addTime = (projectId, seconds) => api.post(`/takeoff/projects/${projectId}/time`, { seconds }).then((r) => r.data);
export const getMyTime = (projectId) => api.get(`/takeoff/projects/${projectId}/time/mine`).then((r) => r.data);
export const getNoteVersions = (projectId, unreadOnly = false) =>
  api.get(`/takeoff/projects/${projectId}/notes/versions${unreadOnly ? "?unread=1" : ""}`).then((r) => r.data);
export const ackNoteVersion = (projectId, versionId) =>
  api.post(`/takeoff/projects/${projectId}/notes/${versionId}/ack`).then((r) => r.data);

/** Remember the sheet this user last had open in the job (server-backed,
 * per user) so reopening the job lands them back on it — any device. */
export const setLastSheet = (projectId, fileId, page) =>
  api.put(`/takeoff/projects/${projectId}/last-sheet`, { file_id: fileId, page }).then((r) => r.data);

/**
 * Upload-time optimization choices (PlanSwift-style): persisted so every
 * upload entry point (panel buttons, header, drag-drop) honours the same
 * ticks until the user changes them.
 */
export const getUploadOptimize = () => {
  try { return JSON.parse(localStorage.getItem("takeoff-upload-optimize") || "{}"); } catch { return {}; }
};
export const setUploadOptimize = (opt) => {
  try { localStorage.setItem("takeoff-upload-optimize", JSON.stringify(opt || {})); } catch { /* ignore */ }
};

/**
 * Chunked drawing upload — 4 MiB parts, parallel workers, resumable. A
 * failed upload keeps its server session; retryUpload() resumes from the
 * last confirmed byte range instead of starting at zero.
 */
export async function uploadDrawing(projectId, file, { folderId = null, category = null, path = "", uploadId = null, onProgress, optimize, pauseSignal = null } = {}) {
  const relPath = path || file.webkitRelativePath || "";
  const opt = optimize !== undefined ? optimize : getUploadOptimize();
  let id = uploadId;
  let confirmed = {};
  if (id) {
    // resume: ask the server which byte ranges it already holds
    const st = await uploadStatus(id).catch(() => null);
    if (!st || !st.chunks) id = null; // session gone — start over
    else confirmed = st.chunks;
  }
  if (!id) {
    const start = await api
      .post(`/takeoff/projects/${projectId}/drawings/start`, {
        name: file.name, size: file.size, mime: file.type || "application/pdf", folderId,
        ...(category ? { category } : {}),
        ...(relPath ? { path: relPath } : {}),
        ...(opt && (opt.dpi400 || opt.tiff) ? { optimize: { dpi400: !!opt.dpi400, tiff: !!opt.tiff } } : {}),
      })
      .then((r) => r.data);
    id = start.upload_id;
  }
  // Checksum policy (Oct-27 upload speed-up): files under CHECKSUM_MIN_BYTES
  // skip the SHA-256 entirely — the server still checks the exact byte size
  // and gap-free chunk map. Bigger files ARE hashed, but the hash runs in
  // parallel with the chunk pump instead of after it, so it adds no wall
  // time on the client.
  const shaPromise = file.size >= CHECKSUM_MIN_BYTES ? sha256File(file) : Promise.resolve(null);
  await pumpChunks(file, `/takeoff/uploads/${id}/chunk`, { onEvent: onProgress, confirmed, correlationId: id, pauseSignal });
  onProgress?.({ pct: 100, done: file.size, total: file.size, verifying: true });
  const sha256 = await shaPromise;
  return await api.post(`/takeoff/uploads/${id}/complete`, sha256 ? { sha256 } : {}).then((r) => r.data);
}

/** Fetch a drawing's bytes (auth-aware) as an object URL for pdf.js / <img>. */
export async function fetchDrawingUrl(drawingId, mime) {
  const r = await api.get(`/takeoff/drawings/${drawingId}/file`, { responseType: "blob" });
  return URL.createObjectURL(new Blob([r.data], { type: mime || "application/pdf" }));
}

/** Fetch a drawing's raw bytes (auth-aware) for pdf.js getDocument({data}). */
export async function fetchDrawingBytes(drawingId) {
  const r = await api.get(`/takeoff/drawings/${drawingId}/file`, { responseType: "arraybuffer" });
  return r.data;
}

/** Poll a background ZIP extraction. */
export const uploadStatus = (uploadId) => api.get(`/takeoff/uploads/${uploadId}`).then((r) => r.data);

/** Single drawing record — polled while background optimization runs. */
export const getDrawing = (drawingId) => api.get(`/takeoff/drawings/${drawingId}`).then((r) => r.data);

/** One page of a TIFF drawing as a web rendition (server-rendered + cached).
 * w is bucketed (2600 fit / 6400 zoom) so the server cache stays small. */
export async function fetchTiffPage(drawingId, page, w) {
  const r = await api.get(`/takeoff/drawings/${drawingId}/file`, { params: { page, w }, responseType: "blob" });
  return URL.createObjectURL(r.data);
}

/** Server-side prepare (readability + page count) after the bytes land —
 * drives the Uploaded -> Processing -> Ready row state. */
export const prepareDrawing = (drawingId) => api.post(`/takeoff/drawings/${drawingId}/prepare`).then((r) => r.data);
export const readRegion = (drawingId, page, rect) =>
  api.post(`/takeoff/drawings/${drawingId}/read-region`, { page, ...rect }).then((r) => r.data);
export const ocrRegion = (drawingId, page, rect) =>
  api.post(`/takeoff/drawings/${drawingId}/ocr-region`, { page, ...rect }).then((r) => r.data);

// ---- complete job backup import (spec §12) ----

/** Stream a backup ZIP to the importer in 4 MiB chunks — resumable: a
 * failed attempt keeps its session and continues from the server's
 * confirmed byte ranges (never restarts at zero). */
export async function uploadImportZip(file, onProgress, importId = null) {
  let id = importId;
  let confirmed = {};
  if (id) {
    const st = await importStatus(id).catch(() => null);
    if (!st || !st.chunks || st.state !== "uploading") id = null;
    else confirmed = st.chunks;
  }
  if (!id) {
    const start = await api.post(`/takeoff/import/start`, { name: file.name, size: file.size }).then((r) => r.data);
    id = start.import_id;
  }
  await pumpChunks(file, `/takeoff/import/${id}/chunk`, { onEvent: onProgress, confirmed, correlationId: id, stage: "import-upload" });
  return id;
}

export const validateImport = (importId) => api.post(`/takeoff/import/${importId}/validate`).then((r) => r.data);
export const commitImport = (importId, opts = {}) => api.post(`/takeoff/import/${importId}/commit`, opts).then((r) => r.data);
export const importStatus = (importId) => api.get(`/takeoff/import/${importId}`).then((r) => r.data);
export const abortImport = (importId) => api.post(`/takeoff/import/${importId}/abort`).then((r) => r.data);

// ---- Developer Centre ----
export const devBuild = () => api.get("/takeoff/dev/build").then((r) => r.data);
export const devTree = () => api.get("/takeoff/dev/tree").then((r) => r.data);
export const devFile = (path) => api.get("/takeoff/dev/file", { params: { path } }).then((r) => r.data);
export const devHistory = () => api.get("/takeoff/dev/history").then((r) => r.data);
export const devDiff = (sha) => api.get("/takeoff/dev/diff", { params: { sha } }).then((r) => r.data);
export const devDiagnostics = () => api.get("/takeoff/dev/diagnostics").then((r) => r.data);
export const devDownload = (which) =>
  api.get(`/takeoff/dev/${which}`, { responseType: "blob" }).then((r) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = which === "source.zip" ? "elite-portal-source.zip" : "elite-portal-debug.zip";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  });

/** Download the priced estimate spreadsheet (optionally scoped by label/item tag). */
export async function downloadEstimateXlsx(projectId, projectName, { label, tag } = {}) {
  const params = {};
  if (label) params.label = label;
  if (tag) params.tag = tag;
  const r = await api.get(`/takeoff/projects/${projectId}/estimate.xlsx`, { responseType: "blob", params });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Estimate - ${(projectName || "project").replace(/\//g, "-")}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

// ---------------- CAD (DWG/DXF) ----------------
/** Layers / pages / units / status of a CAD drawing (or a split sheet). */
export const cadInfo = (drawingId) => api.get(`/takeoff/drawings/${drawingId}/cad`).then((r) => r.data);
/** Kick off (or retry) the DWG -> pages conversion for an uploaded CAD file. */
export const cadPrepare = (drawingId, force = false) => api.post(`/takeoff/drawings/${drawingId}/cad/prepare`, null, { params: force ? { force: 1 } : {} }).then((r) => r.data);
/** Shared layer visibility / drawing units — re-renders the sheet for everyone. */
export const cadUpdate = (drawingId, patch) => api.patch(`/takeoff/drawings/${drawingId}/cad`, patch).then((r) => r.data);
/** Nearest CAD endpoint / midpoint / on-line point within r page units. */
export const cadSnap = (drawingId, page, x, y, r) => api.post(`/takeoff/drawings/${drawingId}/cad/snap`, { page, x, y, r }).then((res) => res.data.snap);
/** The CAD entity under the cursor, with its exact geometry and length/area. */
export const cadPick = (drawingId, page, x, y, r) => api.post(`/takeoff/drawings/${drawingId}/cad/pick`, { page, x, y, r }).then((res) => res.data.entity);
/** Totals per layer for one page (length ft / area sf / block count). */
export const cadQuantities = (drawingId, page) => api.get(`/takeoff/drawings/${drawingId}/cad/quantities`, { params: { page } }).then((r) => r.data);
/** One new drawing per layout sheet (each keeps the layer tools). */
export const cadSplit = (drawingId) => api.post(`/takeoff/drawings/${drawingId}/cad/split`).then((r) => r.data);
/** Download the current PDF / original DWG / converted DXF as an object URL. */
export async function cadDownload(drawingId, kind) {
  const r = await api.get(`/takeoff/drawings/${drawingId}/cad/download`, { params: { kind }, responseType: "blob" });
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(cd);
  return { url: URL.createObjectURL(r.data), filename: m ? decodeURIComponent(m[1]) : `drawing.${kind}` };
}
/** Create an AI-estimate project from the chosen PDFs of this job. */
export const sendToEstimate = (projectId, payload) => api.post(`/takeoff/projects/${projectId}/send-to-estimate`, payload).then((r) => r.data);
/** Drawing tools: add_line / add_polyline / add_text / move / delete / offset (stored, re-rendered, exported as DXF). */
export const cadEdit = (drawingId, edit) => api.post(`/takeoff/drawings/${drawingId}/cad/edits`, edit).then((r) => r.data);
export const cadRemoveEdit = (drawingId, editId, page) => api.delete(`/takeoff/drawings/${drawingId}/cad/edits/${editId}`, { params: { page } }).then((r) => r.data);
/** Named layer views shared with the team. */
export const cadAddPreset = (drawingId, name, hidden) => api.post(`/takeoff/drawings/${drawingId}/cad/presets`, { name, hidden }).then((r) => r.data);
export const cadRemovePreset = (drawingId, presetId) => api.delete(`/takeoff/drawings/${drawingId}/cad/presets/${presetId}`).then((r) => r.data);
